import { useState, useEffect } from 'react';
import { Percent, DollarSign, Gift, Tag, Trash2 } from 'lucide-react';
import { Modal, Button, Tooltip } from './ui';
import { aplicarOferta, validarOferta, moneyFmt } from '../utils/helpers';

/**
 * Modal para aplicar/editar una OFERTA COMERCIAL sobre un servicio o CF.
 *
 * Diferencia con VariacionesModal: variaciones son ajustes INTERNOS de precio
 * (negociación, margen). Ofertas son descuentos COMERCIALES que se destacan al
 * cliente en el PDF con precio tachado + motivo.
 *
 * Props:
 *   - open: bool
 *   - onClose: () => void
 *   - onSave: (oferta | null) => void   // null si "Quitar oferta"
 *   - onDelete: () => void              // shortcut para quitar la oferta
 *   - titulo: string                    // ej. "Aplicar oferta a Hosting VPS"
 *   - precioBase: number                // precio original de la línea (sin oferta)
 *   - moneda: 'PEN' | 'USD'
 *   - ofertaInicial: { modalidad, valor, motivo } | null
 */
const OfertaModal = ({
    open,
    onClose,
    onSave,
    onDelete,
    titulo,
    precioBase = 0,
    moneda = 'PEN',
    ofertaInicial = null
}) => {
    const [modalidad, setModalidad] = useState('porcentaje');
    const [valor, setValor] = useState('');
    const [motivo, setMotivo] = useState('');
    const [errorValor, setErrorValor] = useState('');
    const [errorMotivo, setErrorMotivo] = useState('');

    // Reset al abrir con la oferta que trae desde arriba.
    useEffect(() => {
        if (!open) return;
        setModalidad(ofertaInicial?.modalidad || 'porcentaje');
        setValor(ofertaInicial?.valor != null ? String(ofertaInicial.valor) : '');
        setMotivo(ofertaInicial?.motivo || '');
        setErrorValor('');
        setErrorMotivo('');
    }, [open, ofertaInicial]);

    const ofertaActual = {
        modalidad,
        valor: parseFloat(valor) || 0,
        motivo: motivo.trim()
    };

    const validacionValor = validarOferta(ofertaActual, precioBase, moneda);
    const precioFinal = validacionValor.ok ? aplicarOferta(precioBase, ofertaActual) : precioBase;
    const ahorro = Math.max(0, precioBase - precioFinal);

    const handleGuardar = () => {
        let hayError = false;

        // Validar motivo obligatorio
        if (!motivo.trim()) {
            setErrorMotivo('El motivo es obligatorio');
            hayError = true;
        } else {
            setErrorMotivo('');
        }

        // Validar valor (sólo si no es "gratis" que ignora valor)
        if (modalidad !== 'gratis') {
            if (!valor || parseFloat(valor) <= 0) {
                setErrorValor('Ingresa un valor mayor a 0');
                hayError = true;
            } else if (!validacionValor.ok) {
                setErrorValor(validacionValor.message);
                hayError = true;
            } else {
                setErrorValor('');
            }
        } else {
            setErrorValor('');
        }

        if (hayError) return;
        onSave?.(ofertaActual);
    };

    const opciones = [
        { key: 'porcentaje', icon: Percent, label: '% descuento' },
        { key: 'monto', icon: DollarSign, label: 'Monto fijo' },
        { key: 'gratis', icon: Gift, label: 'Gratis' }
    ];

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <Tag size={16} aria-hidden="true" />
                    {titulo || 'Aplicar oferta'}
                </span>
            }
            size="md"
            footer={
                <div className="oferta-modal-footer">
                    <div className="oferta-modal-footer-left">
                        {ofertaInicial && (
                            <Tooltip content="Eliminar la oferta aplicada a esta línea">
                                <Button variant="danger" size="sm" onClick={onDelete}>
                                    <Trash2 size={14} aria-hidden="true" />
                                    Quitar oferta
                                </Button>
                            </Tooltip>
                        )}
                    </div>
                    <div className="oferta-modal-footer-right">
                        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
                        <Button variant="primary" onClick={handleGuardar}>
                            {ofertaInicial ? 'Actualizar' : 'Aplicar'}
                        </Button>
                    </div>
                </div>
            }
        >
            <div className="oferta-modal-body">
                <div className="oferta-precio-base">
                    <span className="label">Precio base</span>
                    <span className="valor">{moneyFmt(precioBase, moneda)}</span>
                </div>

                {/* Selector de modalidad — 3 pills mutuamente exclusivas */}
                <div className="field">
                    <span className="label">Tipo de oferta</span>
                    <div className="oferta-modalidad-group" role="radiogroup" aria-label="Tipo de oferta">
                        {opciones.map(op => {
                            const Icon = op.icon;
                            const activa = modalidad === op.key;
                            return (
                                <button
                                    key={op.key}
                                    type="button"
                                    role="radio"
                                    aria-checked={activa}
                                    className={`oferta-modalidad-pill ${activa ? 'is-active' : ''}`}
                                    onClick={() => {
                                        setModalidad(op.key);
                                        if (op.key === 'gratis') { setValor(''); setErrorValor(''); }
                                    }}
                                >
                                    <Icon size={14} aria-hidden="true" />
                                    <span>{op.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Input de valor: se muestra solo si no es "gratis" */}
                {modalidad !== 'gratis' && (
                    <label className="field">
                        <span className="label">
                            Valor {modalidad === 'porcentaje' ? '(0-100 %)' : `(máx. ${moneyFmt(precioBase, moneda)})`}
                        </span>
                        <input
                            className={`input ${errorValor ? 'is-error' : ''}`}
                            type="number"
                            min="0"
                            max={modalidad === 'porcentaje' ? 100 : precioBase}
                            step={modalidad === 'porcentaje' ? '0.1' : '0.01'}
                            value={valor}
                            onChange={(e) => { setValor(e.target.value); setErrorValor(''); }}
                            placeholder={modalidad === 'porcentaje' ? 'Ej. 25' : 'Ej. 50.00'}
                        />
                        {errorValor && <span className="field-error">{errorValor}</span>}
                    </label>
                )}

                <label className="field">
                    <span className="label">
                        Motivo <span className="text-muted">(visible al cliente)</span>
                    </span>
                    <textarea
                        className={`input ${errorMotivo ? 'is-error' : ''}`}
                        rows={2}
                        maxLength={200}
                        value={motivo}
                        onChange={(e) => { setMotivo(e.target.value); setErrorMotivo(''); }}
                        placeholder='Ej. Gratis 1er año, Descuento combo, Promo aniversario'
                    />
                    <div className="field-meta">
                        {errorMotivo && <span className="field-error">{errorMotivo}</span>}
                        <span className="field-counter">{motivo.length}/200</span>
                    </div>
                </label>

                {/* Preview del precio final */}
                <div className="oferta-preview">
                    <div className="oferta-preview-row">
                        <span className="label">Precio final</span>
                        <div className="oferta-preview-precios">
                            {ahorro > 0 && (
                                <span className="oferta-preview-tachado">{moneyFmt(precioBase, moneda)}</span>
                            )}
                            <span className="oferta-preview-final">{moneyFmt(precioFinal, moneda)}</span>
                        </div>
                    </div>
                    {ahorro > 0 && (
                        <div className="oferta-preview-ahorro">
                            Ahorro: <strong>{moneyFmt(ahorro, moneda)}</strong>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default OfertaModal;
